import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { colorPalette } from "../styles/theme";
import { useWsDataset, buildOverviewSummary, getQuickRange } from "./wsData";
import TriageTable from "./TriageTable";
import OverviewCharts from "./OverviewCharts";

/**
 * 종합현황 화면 (WS 샘플 — 실데이터 CSV 기반)
 * 1. 조회 기간 컨트롤 바 — 퀵 버튼(최근 7/14/30일) + 시작일~종료일 직접 조회
 * 2. KPI 요약 카드 4종 — 기간이 바뀌면 일괄 갱신
 *
 * buildOverviewSummary()(wsData.js)가 CSV를 서버 응답과 같은 필드명으로 집계합니다.
 * "최근 n일"의 기준일은 데이터의 마지막 날짜입니다.
 */

const QUICK_RANGES = [7, 14, 30];
const DEFAULT_RANGE_DAYS = 7;

const ALERT_COLOR = "#EF4444";

// KPI 카드 배치 기준 — 카드 1장이 최소 약 260px을 확보할 수 있는 본문 폭 (카드 간격 16px 포함)
const KPI_FOUR_COLUMN_MIN_WIDTH = 4 * 260 + 3 * 16;
const KPI_TWO_COLUMN_MIN_WIDTH = 2 * 260 + 16;

// 기관 평균 디스트레스(0~1) 4단계 상태 — 개인 상세 DISTRESS_LEVELS와 같은 구간 기준
const MIND_STATES = [
  { max: 0.16, label: "안정", color: "#16a34a", bg: "#dcfce7" },
  { max: 0.41, label: "주의", color: "#ca8a04", bg: "#fef9c3" },
  { max: 0.6, label: "경고", color: "#ea580c", bg: "#ffedd5" },
  { max: 1, label: "위험", color: ALERT_COLOR, bg: "#fee2e2" },
];
const NO_RECORD_STATE = { label: "기록 없음", color: "#9CA3AF" };
const getMindStateIndex = (value) => {
  const index = MIND_STATES.findIndex((state) => value <= state.max);
  return index === -1 ? MIND_STATES.length - 1 : index;
};

// ─── 날짜 유틸 ─────────────────────────────────────────────────────
const parseDate = (str) => {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const daysBetween = (start, end) => Math.round((parseDate(end) - parseDate(start)) / 86400000) + 1;

// 기간 퀵 범위는 데이터 마지막 날짜 기준 (ReportView에서도 함께 사용)
export { getQuickRange };

// ─── 조회 기간 컨트롤 바 ────────────────────────────────────────────
export const PeriodControlBar = ({ appliedRange, onApply }) => {
  const [rangeStart, setRangeStart] = useState(appliedRange.start);
  const [rangeEnd, setRangeEnd] = useState(appliedRange.end);

  // 현재 적용된 기간이 퀵 범위와 정확히 일치할 때만 해당 퀵 버튼을 활성 표시
  const activeQuickDays = QUICK_RANGES.find((days) => {
    const quick = getQuickRange(days);
    return quick.start === appliedRange.start && quick.end === appliedRange.end;
  });

  const isInvalidRange = !rangeStart || !rangeEnd || rangeStart > rangeEnd;

  const handleQuickSelect = (_, days) => {
    if (!days) return;
    const range = getQuickRange(days);
    setRangeStart(range.start);
    setRangeEnd(range.end);
    onApply(range);
  };

  const handleSearch = () => {
    if (isInvalidRange) return;
    onApply({ start: rangeStart, end: rangeEnd });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        px: 2.5,
        py: 1.5,
        mb: 3,
        backgroundColor: colorPalette.white,
      }}
    >
      {/* 왼쪽: 현재 적용된 조회 기간 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <EventOutlinedIcon sx={{ fontSize: 18, color: colorPalette.primary }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary }}>조회 기간</Typography>
        <Typography sx={{ fontSize: 13, color: colorPalette.textSecondary }}>
          {appliedRange.start} ~ {appliedRange.end}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            color: colorPalette.primary,
            backgroundColor: colorPalette.activeBg,
          }}
        >
          {daysBetween(appliedRange.start, appliedRange.end)}일
        </Typography>
      </Box>

      {/* 오른쪽: 퀵 버튼 + 날짜 직접 조회 (개인 상세 날짜 조회와 동일 규격) */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, ml: "auto" }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeQuickDays ?? null}
          onChange={handleQuickSelect}
          sx={{
            "& .MuiToggleButton-root": {
              px: 1.5,
              py: 0.55,
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              color: colorPalette.textSecondary,
              borderColor: colorPalette.border,
            },
            "& .Mui-selected": {
              color: `${colorPalette.primary} !important`,
              backgroundColor: `${colorPalette.activeBg} !important`,
            },
          }}
        >
          {QUICK_RANGES.map((days) => (
            <ToggleButton key={days} value={days}>
              최근 {days}일
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            type="date"
            size="small"
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-input": { fontSize: 13, py: 0.7 } }}
          />
          <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
            ~
          </Typography>
          <TextField
            type="date"
            size="small"
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ "& .MuiInputBase-input": { fontSize: 13, py: 0.7 } }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={isInvalidRange}
            onClick={handleSearch}
            sx={{ fontSize: 12, px: 1.5 }}
          >
            조회
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// ─── KPI 요약 카드 ─────────────────────────────────────────────────
/** 카드 공통 틀 — 아이콘+제목 / (우측 상단 액션) / 메인 / 하단 보조텍스트 */
const KpiCard = ({ Icon, title, alert, onClick, footer, children }) => {
  const clickable = Boolean(onClick);
  return (
    <Paper
      variant="outlined"
      component={clickable ? ButtonBase : "div"}
      onClick={onClick}
      sx={{
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        gap: 2,
        textAlign: "left",
        backgroundColor: colorPalette.white,
        borderColor: alert ? ALERT_COLOR : colorPalette.border,
        borderWidth: alert ? 1.5 : 1,
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        ...(clickable && {
          cursor: "pointer",
          "&:hover": {
            borderColor: alert ? ALERT_COLOR : colorPalette.primary,
            boxShadow: "0 4px 14px rgba(26, 31, 54, 0.08)",
          },
        }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1.5,
            color: alert ? ALERT_COLOR : colorPalette.primary,
            backgroundColor: alert ? "#fee2e2" : colorPalette.activeBg,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary }}>
          {title}
        </Typography>
        {clickable && <ChevronRightIcon sx={{ fontSize: 18, color: colorPalette.textSecondary }} />}
      </Box>

      <Box sx={{ flex: 1 }}>{children}</Box>

      <Box sx={{ pt: 1.5, borderTop: `1px solid ${colorPalette.border}` }}>
        <Typography component="div" sx={{ fontSize: 12, color: colorPalette.textSecondary }}>
          {footer}
        </Typography>
      </Box>
    </Paper>
  );
};

/** 큰 숫자 + 단위 */
const BigValue = ({ value, unit, color = colorPalette.textPrimary }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.25 }}>
    <Typography component="span" sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1, color }}>
      {value}
    </Typography>
    <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color }}>
      {unit}
    </Typography>
  </Box>
);

/** 누적 검사 횟수 옆 기간 내 신규 건수 (▲n) */
const WeekNew = ({ value }) => (
  <Box component="span" sx={{ fontWeight: 700, color: value ? "#f59e0b" : colorPalette.textSecondary }}>
    {value ? `(▲${value})` : "(-)"}
  </Box>
);

/** card1) 기간 내 활동 참여율 — 클릭 시 참가자 관리(잠잠 필터)로 이동 */
const ParticipationCard = ({ summary, onClick }) => {
  const active = summary.org_respondents;
  const inactive = summary.org_total - active;
  const rate = summary.org_total ? Math.round((active / summary.org_total) * 100) : 0;

  return (
    <KpiCard
      Icon={GroupsOutlinedIcon}
      title="기간 내 활동 참여율"
      onClick={onClick}
      footer={
        <>
          활발 <b>{active}명</b> · 잠잠(미활동) <b style={{ color: inactive ? ALERT_COLOR : undefined }}>{inactive}명</b>
        </>
      }
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <BigValue value={rate} unit="%" />
        <Typography sx={{ fontSize: 13, color: colorPalette.textSecondary }}>
          ({active}명/총 {summary.org_total}명)
        </Typography>
      </Box>
      <Box sx={{ mt: 1.5, height: 6, borderRadius: 3, backgroundColor: colorPalette.secondary, overflow: "hidden" }}>
        <Box
          sx={{
            width: `${Math.min(rate, 100)}%`,
            height: "100%",
            borderRadius: 3,
            backgroundColor: colorPalette.primary,
            transition: "width 0.3s ease",
          }}
        />
      </Box>
    </KpiCard>
  );
};

/** card2) 선별검사(PHQ, GAD) 현황 — 우측 상단 [미검사자 일괄 요청] */
const ScreeningCard = ({ summary }) => {
  const [requested, setRequested] = useState(false);
  const count = summary.unexamined_count;

  // 실제 연동 시 미검사자 전체에 POST /users/survey-notification 발송 (개인 상세 검사 요청과 동일 흐름)
  const handleBulkRequest = () => {
    if (!window.confirm(`미검사자 ${count}명에게 PHQ-9 · GAD-7 검사 요청을 발송하시겠습니까?`)) return;
    setRequested(true);
    alert(`(샘플) 미검사자 ${count}명에게 검사 요청이 발송되었습니다.`);
  };

  return (
    <KpiCard
      Icon={AssignmentLateOutlinedIcon}
      title="선별검사(PHQ, GAD) 현황"
      footer={
        // 줄이 넘칠 때 "누적 PHQ-9 n회 (▲n)" 단위로만 끊기도록 항목별 nowrap
        <>
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            누적 PHQ-9 <b>{summary.phq.total}회</b> <WeekNew value={summary.phq.week_new} />
          </Box>
          {" · "}
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            GAD-7 <b>{summary.gad.total}회</b> <WeekNew value={summary.gad.week_new} />
          </Box>
        </>
      }
    >
      {/* 카드 폭이 좁아 제목 옆 대신 메인 수치 줄의 우측 상단에 버튼 배치 */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: colorPalette.textSecondary }}>미검사</Typography>
          <BigValue value={count} unit="명" />
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={count === 0 || requested}
          onClick={handleBulkRequest}
          sx={{ flexShrink: 0, height: 28, px: 1.25, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}
        >
          {requested ? "요청됨" : "미검사자 일괄 요청"}
        </Button>
      </Box>
    </KpiCard>
  );
};

/** card3) 집중 케어 신호(에피소드) — 1건 이상이면 테두리·숫자 붉은색 강조 */
const CareSignalCard = ({ summary }) => {
  const crisis = summary.suicide_risk_count;
  const episodes = summary.episode_total;
  const alert = crisis > 0 || episodes > 0;
  const valueColor = alert ? ALERT_COLOR : colorPalette.textPrimary;

  return (
    <KpiCard
      Icon={ReportProblemOutlinedIcon}
      title="집중 케어 신호(에피소드)"
      alert={alert}
      footer={
        <>
          기간 내 급성 악화(에피소드) 발생 인원: <b>{summary.episode_user_count}명</b>
        </>
      }
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colorPalette.textSecondary }}>긴급 위기</Typography>
        <BigValue value={crisis} unit="명" color={crisis > 0 ? ALERT_COLOR : colorPalette.textPrimary} />
        <Typography sx={{ fontSize: 20, color: colorPalette.border }}>/</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colorPalette.textSecondary }}>에피소드</Typography>
        <BigValue value={episodes} unit="건" color={episodes > 0 ? valueColor : colorPalette.textPrimary} />
      </Box>
    </KpiCard>
  );
};

/** card4) 기관 평균 마음 상태 — org_distress_avg를 4단계 상태 텍스트로 치환(소수점 비노출) */
const MindStateCard = ({ summary }) => {
  // 기간 내 디스트레스 기록이 하나도 없으면 단계 대신 "기록 없음" 표시
  const hasValue = typeof summary.org_distress_avg === "number";
  const stateIndex = hasValue ? getMindStateIndex(summary.org_distress_avg) : -1;
  const state = hasValue ? MIND_STATES[stateIndex] : NO_RECORD_STATE;

  return (
    <KpiCard
      Icon={FavoriteBorderOutlinedIcon}
      title="기관 평균 마음 상태"
      footer={
        <>
          현재 디스트레스 고위험군: <b>{summary.distress_high_risk_count}명</b>
        </>
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: state.color }} />
        <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1, color: state.color }}>
          {state.label}
        </Typography>
        {hasValue && (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: colorPalette.textSecondary }}>단계</Typography>
        )}
      </Box>

      {/* 4단계 중 현재 위치 표시 */}
      <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: `repeat(${MIND_STATES.length}, 1fr)`, gap: 0.5 }}>
        {MIND_STATES.map((s, index) => (
          <Box key={s.label}>
            <Box
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: index === stateIndex ? s.color : colorPalette.secondary,
              }}
            />
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 10,
                textAlign: "center",
                fontWeight: index === stateIndex ? 700 : 500,
                color: index === stateIndex ? s.color : colorPalette.textSecondary,
              }}
            >
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </KpiCard>
  );
};

/**
 * @param {string} scopeKey - 조회 범위 키 ("ALL" = 전체 기관, 그 외 사업 code)
 * @param {Function} onNavigate - 다른 메뉴로 이동 (menuKey, params) => void
 */
const OverviewView = ({ scopeKey, onNavigate }) => {
  const dataset = useWsDataset();
  const [appliedRange, setAppliedRange] = useState(() => getQuickRange(DEFAULT_RANGE_DAYS));

  const summary = useMemo(
    () => buildOverviewSummary(dataset, scopeKey, appliedRange.start, appliedRange.end),
    [dataset, scopeKey, appliedRange],
  );

  // 조회 범위/기간이 바뀌면 일괄 요청 버튼 상태도 새로 시작하도록 key로 리셋
  const periodKey = `${scopeKey}|${appliedRange.start}|${appliedRange.end}`;

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 4 }}>
      <PeriodControlBar appliedRange={appliedRange} onApply={setAppliedRange} />

      {/* 카드 최소 폭(약 260px)을 지키도록 본문 폭에 따라 4열 → 2열 → 1열로 재배치 (컨테이너 쿼리 기준) */}
      <Box sx={{ containerType: "inline-size" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 2,
            [`@container (max-width: ${KPI_FOUR_COLUMN_MIN_WIDTH - 1}px)`]: {
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            },
            [`@container (max-width: ${KPI_TWO_COLUMN_MIN_WIDTH - 1}px)`]: {
              gridTemplateColumns: "minmax(0, 1fr)",
            },
          }}
        >
          <ParticipationCard
            summary={summary}
            onClick={() => onNavigate?.("participants", { activity: "inactive" })}
          />
          <ScreeningCard key={periodKey} summary={summary} />
          <CareSignalCard summary={summary} />
          <MindStateCard summary={summary} />
        </Box>
      </Box>

      <TriageTable
        key={periodKey}
        scopeKey={scopeKey}
        range={appliedRange}
        summary={summary}
        onOpenDetail={(row) =>
          onNavigate?.("participants", {
            participant: { id: row.id, name: row.name, age: row.age, gender: row.gender },
          })
        }
      />

      <OverviewCharts
        scopeKey={scopeKey}
        range={appliedRange}
        summary={summary}
        onSelectDistressLevel={(level) => onNavigate?.("participants", { distress: level })}
      />
    </Box>
  );
};

export default OverviewView;
